import React from 'react';
import SbButton from '@Components/Button';
import Divider from '@Components/Divider';
import { Delete } from '@mui/icons-material';
import { Grid, Typography } from '@mui/material';
import { PricingDataType, PricingDetail, TaxPricingDetail } from '@Reducers/AdminShipment/Type';
import { PricingType } from '@Reducers/GetRates/Type';
import { formatNumber, useLocale } from '../../../../utils/formatNumber';
import { Carrier, getCarrierNameById } from './constants';

export default function Charges(props: {
  totalPricing: number;
  pricing: PricingDataType[] | PricingType[] | PricingDetail[];
  taxPricing: PricingDataType[] | PricingType[] | TaxPricingDetail[];
  extraCharge?: PricingDataType[];
  currency: string;
  disable?: boolean;
  writeAccess?: boolean;
  label?: string;
  onDeleteExtraCharge?: ({ chargeName, value }: { chargeName: string; value: number }) => void;
  serviceName?: string;
  carriers?: Carrier[];
}) {
  const locale = useLocale();

  // Combine pricing and taxPricing data
  const combinedPricing = [
    ...(props.pricing as unknown as (PricingDataType | PricingType | PricingDetail)[]),
    ...(props.taxPricing as unknown as (PricingDataType | PricingType | TaxPricingDetail)[]),
  ];

  let invoicedCharges: (PricingDataType | PricingType | PricingDetail | TaxPricingDetail)[] = [];
  let uninvoicedCharges: (PricingDataType | PricingType | PricingDetail | TaxPricingDetail)[] = [];

  if (props.label === 'Purchase - Billed') {
    invoicedCharges = combinedPricing.filter((p: any) => Boolean(p?.invoiced));
    uninvoicedCharges = combinedPricing.filter((p: any) => !p?.invoiced);
  }

  // Calculate totals for invoiced and uninvoiced charges
  const invoicedTotal = invoicedCharges.reduce((sum, item: any) => sum + (item.value || 0), 0);
  const uninvoicedTotal = uninvoicedCharges.reduce((sum, item: any) => sum + (item.value || 0), 0);

  const renderCharges = (charges: (PricingDataType | PricingType | PricingDetail | TaxPricingDetail)[]) =>
    charges.map((item, index) => {
      const { displayKey, value, _carrierId } = item as PricingDataType;
      const rateType =
        (item as PricingDataType).rateType ||
        (item as PricingDetail).rateType ||
        (item as TaxPricingDetail).rateType ||
        undefined;
      const invoiced = (item as PricingDataType).invoiced ?? false;
      const carrierName = props.carriers && getCarrierNameById(props.carriers, _carrierId);

      return (
        <SingleCharge
          key={`pricing-${index}`}
          displayKey={displayKey}
          value={formatNumber(value, locale)}
          isDelete={props.disable ? false : rateType === 'extra'}
          onDeleteExtraCharge={
            rateType === 'extra' ? () => props?.onDeleteExtraCharge?.({ chargeName: displayKey, value }) : undefined
          }
          invoiced={invoiced}
          writeAccess={props.writeAccess}
          label={props.label}
          serviceName={carrierName || undefined}
        />
      );
    });

  return (
    <>
      <Grid container justifyContent={'space-between'}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'common.black' }}>
          {props.label ? props.label : 'Charges'}
        </Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: 'common.black' }}>Amount</Typography>
      </Grid>

      <Divider className="my-1" />

      {props.label === 'Purchase - Billed' ? (
        <>
          {/* ✅ Show Invoiced First */}
          {invoicedCharges.length > 0 && (
            <>
              {renderCharges(invoicedCharges)}
              {/* Invoiced Total */}
              <Divider className="my-1" />
              <Grid container justifyContent={'space-between'}>
                <Typography className="font-medium md-text">Billed Total</Typography>
                <Typography className="font-semibold md-text">
                  {props.currency} {formatNumber(invoicedTotal, locale)}
                </Typography>
              </Grid>
            </>
          )}

          {/* ✅ Divider and Heading for Uninvoiced */}
          {uninvoicedCharges.length > 0 && (
            <>
              <Divider className="my-2" />
              <Typography className="md-text font-semibold mb-1">Purchase - Unbilled</Typography>
              {renderCharges(uninvoicedCharges)}
              {/* Uninvoiced Total */}
              <Divider className="my-1" />
              <Grid container justifyContent={'space-between'}>
                <Typography className="font-medium md-text">Unbilled Total</Typography>
                <Typography className="font-semibold md-text">
                  {props.currency} {formatNumber(uninvoicedTotal, locale)}
                </Typography>
              </Grid>
            </>
          )}
        </>
      ) : (
        // Default render for non-"Purchase - Billed"
        renderCharges(combinedPricing)
      )}

      {/* Extra Charges */}
      {props.extraCharge?.length ? (
        props.extraCharge.map(({ displayKey, value }, index) => (
          <SingleCharge
            key={`extra-charge-${index}`}
            displayKey={displayKey}
            value={value}
            writeAccess={props.writeAccess}
            label={props.label}
          />
        ))
      ) : (
        <></>
      )}

      <Divider className="my-1" />
      <Grid container justifyContent={'space-between'} sx={{ marginY: '10px' }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'common.black' }}>Total</Typography>
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: 'common.black' }}>
          {props.currency} {formatNumber(props.totalPricing, locale)}
        </Typography>
      </Grid>
      <Divider className="my-1" />
    </>
  );
}

function SingleCharge(props: {
  displayKey: string;
  value: number | string;
  isDelete?: boolean;
  writeAccess?: boolean;
  onDeleteExtraCharge?: () => void;
  invoiced?: boolean;
  label?: string;
  serviceName?: string;
}) {
  const { displayKey, value, isDelete, invoiced, writeAccess, label, serviceName } = props;
  return (
    <Grid container justifyContent={'space-between'} alignItems="center" className="mb-1">
      <Grid item xs={10} lg={9}>
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black' }}>
          {displayKey}{' '}
          {label === 'Purchase - Billed' && serviceName && (
            <Typography variant="body2" component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5 }}>
              ({serviceName})
            </Typography>
          )}
          {label === 'Purchase - Billed' && invoiced && (
            <Typography variant="body2" component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', ml: 0.5 }}>
              - Invoiced
            </Typography>
          )}
          {!invoiced && isDelete && (
            <SbButton
              size="small"
              startIcon={<Delete fontSize="small" />}
              className="ml-2 sb-text-gray xs-text"
              sx={{ height: 24, borderRadius: '4px !important' }}
              onClick={props.onDeleteExtraCharge}
              disabled={!writeAccess}
            >
              Delete
            </SbButton>
          )}
        </Typography>
      </Grid>
      <Grid item xs={2} lg={3}>
        <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'common.black', textAlign: 'right' }}>
          {value}
        </Typography>
      </Grid>
    </Grid>
  );
}
